import { Certification } from '@/lib/api';

/**
 * Check if all requirements are met for certificate approval
 */
export const areAllRequirementsMet = (certification: Certification): boolean => {
  const clinicalSatisfied = !certification.clinical || 
    certification.clinical.key === 'COMPLETED' || 
    certification.clinical.key === 'NOT_REQUIRED';
  
  return (
    certification.theory?.key === 'COMPLETED' &&
    clinicalSatisfied &&
    certification.payment?.key === 'COMPLETED' &&
    certification.documentation?.key === 'APPROVED' &&
    certification.graduation?.key === 'APPROVED'
  );
};

/**
 * Calculate overall progress percentage
 */
export const calculateProgress = (certification: Certification): number => {
  const fields = ['theory', 'clinical', 'payment', 'documentation', 'graduation'] as const;
  const completed = fields.filter(field => {
    const fieldData = certification[field];
    if (field === 'theory' || field === 'payment') {
      return fieldData?.key === 'COMPLETED';
    }
    if (field === 'clinical') {
      // Missing clinical or NOT_REQUIRED counts as completed (100% progress)
      return !fieldData || 
        fieldData.key === 'COMPLETED' || 
        fieldData.key === 'NOT_REQUIRED';
    }
    return fieldData?.key === 'APPROVED';
  }).length;
  
  return Math.round((completed / fields.length) * 100);
};

/**
 * Get next steps for the student
 */
export const getNextSteps = (certification: Certification): string[] => {
  const steps: string[] = [];
  
  if (certification.theory?.key !== 'COMPLETED') {
    steps.push('Complete theory requirements');
  }
  // Only show clinical step if it exists and is not completed or not required
  if (certification.clinical && 
      certification.clinical.key !== 'COMPLETED' && 
      certification.clinical.key !== 'NOT_REQUIRED') {
    steps.push('Complete clinical requirements');
  }
  if (certification.payment?.key !== 'COMPLETED') {
    steps.push('Complete payment requirements');
  }
  if (certification.documentation?.key !== 'APPROVED') {
    steps.push('Submit documentation for approval');
  }
  if (certification.graduation?.key !== 'APPROVED') {
    steps.push('Graduation approval pending');
  }
  
  if (steps.length === 0) {
    if (certification.certificate?.status === 'APPROVED') {
      steps.push('Certificate approved - download available');
    } else if (certification.certificate?.status === 'REJECTED') {
      steps.push(`Certificate rejected: ${certification.certificate.rejectionReason || 'No reason provided'}`);
    } else {
      steps.push('All requirements met - waiting for certificate approval');
    }
  }
  
  return steps;
};

/**
 * Get certificate status variant for UI styling
 */
export const getCertificateStatusVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (status) {
    case 'ISSUED':
      return 'default';
    case 'APPROVED':
      return 'secondary';
    case 'REJECTED':
      return 'destructive';
    case 'PENDING':
      return 'outline';
    default:
      return 'outline';
  }
};

/**
 * Get custom badge styling classes for certificate status with colors
 */
export const getCertificateStatusBadgeClass = (status?: string): string => {
  switch (status) {
    case 'ISSUED':
      return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 shadow-sm';
    case 'APPROVED':
      return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200 shadow-sm';
    case 'REJECTED':
      return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200 shadow-sm';
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200 shadow-sm';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200 shadow-sm';
  }
};

/**
 * Check if certificate can be approved
 */
export const canApproveCertificate = (certification: Certification): boolean => {
  return areAllRequirementsMet(certification) && 
         certification.certificate?.status !== 'APPROVED' &&
         certification.certificate?.status !== 'REJECTED';
};
